/* eslint-disable @nx/enforce-module-boundaries */
'use client';
import { AddIcon } from '@chakra-ui/icons';
import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, useDisclosure } from '@chakra-ui/react';
import { Box, Flex, HStack, Link, Text, VStack } from '@mybridge/ui';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDir } from 'packages/mybridgev4/context/direction-context';
import { forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadDictionary, setLanguage } from 'v4/store/slices/translationSlice';
export const MainCopyrightsText = forwardRef(({alignment, ...props }, ref) => {
  const { t } = useTranslator();
  const direction = useDirection();
  const dispatch = useDispatch();
  const { setDirection } = useDir();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const { loggedIn } = useSelector((state) => state.user);
  const languageOptions = [
    { code: 'en', name: `English` },
    { code: 'fr', name: `French - Français` },
    { code: 'de', name: `German - Deutsch` },
    { code: 'pt', name: `Portuguese - Português` },
    { code: 'es', name: `Spanish - Español` },
    { code: 'hi', name: `Hindi - हिंदी` },
    { code: 'zh-cn', name: `Chinese - 中文` },
    { code: 'ja', name: `Japanese - 日本語` },
    { code: 'ko', name: `Korean - 한국어` },
    { code: 'ru', name: `Russian - Русский` },
    { code: 'tr', name: `Turkish - Türkçe` },
    { code: 'ar', name: `Arabic - العربية` },
    { code: 'id', name: 'Indonesian - Bahasa Indonesia' },
    { code: 'vi', name: 'Vietnamese - Tiếng Việt' },
    { code: 'it', name: 'Italian - Italiano' }
  ];
  const getLanguageDisplayName = (code) => {
    const language = languageOptions.find(lang => lang.code === code);
    return language ? language?.name : 'English'; // Return just the English name
  };
  useEffect(() => {
    // Initialize selected language from localStorage
    const savedLang = localStorage.getItem('userLanguage') || 'en';
    setSelectedLanguage(savedLang);
  }, []);
  
  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    localStorage.setItem('userLanguage', lang);
    dispatch(loadDictionary(lang));
    dispatch(setLanguage(lang));
    const isRTL = ['ar'].includes(lang);
    setDirection(isRTL ? 'rtl' : 'ltr');
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      document.documentElement.setAttribute('lang', lang);
    }
    onClose();
  };
  return (
    <>
      <VStack
        textAlign="center"
        fontSize="xs"
        color="gray.500"
        {...props}
        alignItems={alignment}
        mt={3}
      >
        <VStack gap={'2px'}>
          <Flex flexWrap="wrap" justifyContent="center" rowGap={1}>
            <Text display="inline" as={Link} href="/terms-of-use">
              {t('TnC.termsConditions') || "Terms & Conditions"}
            </Text>
            <Text mx={1} display="inline">{' · '}</Text>
            <Text display="inline" as={Link} href="/privacy-policy">
              {t('TnC.privacyPolicy') || "Privacy Policy"}
            </Text>
            <Text mx={1} display="inline">{' · '}</Text>
            <Text display="inline" as={Link} href="/cookie-policy">
              {"Cookie Policy"}
            </Text>
          </Flex>
          <Text display="inline" as={Link} href="/sms-messaging-consent">
            {"SMS Messaging & Consent Policy"}
          </Text>
          {!loggedIn && <Box
            variant="ghost"
            onClick={onOpen}
            mr={2}
            cursor='pointer'
          >
            <Text color="brandGray.700" me="10px">

              {getLanguageDisplayName(selectedLanguage)} | {t('settingAccount.chooseLanguage')||"Choose Language"}
            </Text>
          </Box>}
        </VStack>
        <Box mt="-6px">
          <Text display="inline" as={Link} href="/business">
            {t('navigation.business') || "Business"}
          </Text>
          <Text display="inline">{' · '}</Text>
          <Text display="inline" as={Link} href="/support">
            {t('TnC.helpSupport') || "Help & Support"}
          </Text>
          <Text display="inline">{' · '}</Text>
          <Text display="inline" as={Link} href="https://forms.gle/BYZU66c2rNptvedH8">
            {"Feedback"}
          </Text>
        </Box>
        <Text> myBridge {t('TnC.corporation') || "Corporation"} © {new Date().getFullYear()}</Text>
        {/* Language Selection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('settingAccount.chooseLanguage')||"Choose Language"}</ModalHeader>
          <ModalCloseButton {...(direction === 'rtl') ? {left: '0.75rem', right: 'auto'} : {left: 'auto', right: '0.75rem'}} />
          <ModalBody>
              <Stack spacing={3}>
                {languageOptions.map((lang) => (
                  <Button
                    key={lang.code}
                    variant={selectedLanguage === lang.code ? "solid" : "outline"}
                    colorScheme={selectedLanguage === lang.code ? "blue" : "gray"}
                    onClick={() => handleLanguageChange(lang.code)}
                    w="100%"
                  >
                    {lang.name}
                  </Button>
                ))}
              </Stack>
            </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('buttons.cancel') || "Cancel"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      </VStack>
    </>
  );
});
